/**
 * Who owns the document-global ``chunk-evidence`` highlight.
 *
 * The CSS Custom Highlight registry is per-DOCUMENT — ``CSS.highlights`` is a
 * single map keyed by name — while ``AppShell`` keeps EVERY tab mounted
 * (`hidden={!active}`, one pane per tab, ``TAB_CAP = 8``). So several
 * ``DrugDetailPage`` instances are alive at once, all of them reading and
 * writing the same ``chunk-evidence`` entry.
 *
 * Without an owner, two things go wrong and both are silent:
 *
 *   1. A tab's unmount cleanup (``DrugDetailPage``'s "clean up CSS highlights
 *      on unmount" effect) deletes the entry — including when the highlight on
 *      screen belongs to a DIFFERENT tab. At the tab cap every citation click
 *      evicts a tab, so this fires during normal use.
 *   2. A second tab highlighting replaces the first tab's ranges, and the first
 *      tab keeps rendering as if it still had one.
 *
 * The name cannot simply be made per-tab: ``::highlight(chunk-evidence)`` is a
 * static selector in the stylesheet, so a dynamic name would lose its styling.
 * Ownership is the smaller fix — one writer at a time, and only that writer may
 * clear.
 *
 * ponytail: module-level single owner, not a per-document map. There is one
 * document per browser tab; upgrade to a WeakMap keyed by Document only if
 * these viewers are ever rendered into a second document (popout window).
 */

let owner: string | null = null;

/** Record that ``tabId`` has just written the highlight. */
export function claimChunkHighlight(tabId: string): void {
  owner = tabId;
}

/**
 * May ``tabId`` delete the current highlight?
 *
 * Unowned is clearable by anyone: nothing is on screen to destroy. Owned is
 * clearable only by its owner — which is what stops an evicted background tab
 * from wiping the highlight the reader is actually looking at.
 */
export function mayClearChunkHighlight(tabId: string): boolean {
  return owner === null || owner === tabId;
}

/** Give up ownership, if held. Clearing someone else's is a no-op. */
export function releaseChunkHighlight(tabId: string): void {
  if (owner === tabId) owner = null;
}

/** Test seam only — the module keeps state across a test file otherwise. */
export function __resetChunkHighlightOwner(): void {
  owner = null;
}
